import Modal from "@/components/atoms/Modal";
import Select, { SelectPaginateAsync } from "@/components/atoms/Select";
import { EMAIL_REGEX } from "@/constants/regexs";
import {
  INVALID_EMAIL2_MESSAGE,
  INVALID_FIRSTNAME_MESSAGE,
  INVALID_FORM_MESSAGE,
  INVALID_LASTNAME_MESSAGE,
  INVALID_PHONE2_MESSAGE,
} from "@/constants/validatorMessages";
import useLoadOptions from "@/hooks/useLoadOptions";
import { CompanyCourseFilters } from "@/interfaces/CompanyCourse";
import { IModalMethods } from "@/interfaces/Modal";
import { GENDER_TYPES, PersonForm } from "@/interfaces/Person";
import { useStorePersonMutation } from "@/services/people";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { Col, Form, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";

const StorePersonModal: ForwardRefRenderFunction<
  IModalMethods,
  { companyId: string; courseId?: string }
> = ({ companyId, courseId }, ref) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const [storeCustomerAsync] = useStorePersonMutation();
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PersonForm>({
    defaultValues: {
      companyId,
      courseId,
    },
  });
  const { loadCompanyCoursesOptions } = useLoadOptions();

  const defaultAdditional = useRef<CompanyCourseFilters>({
    pageIndex: 0,
    pageSize: 10,
    companyId,
  }).current;

  const showOrHiddenModal = () => ModalRef.current?.showOrHiddenModal();

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const onSubmit: SubmitHandler<PersonForm> = ({
    phone,
    gender,
    companyCourses,
    ...data
  }) => {
    showOrHiddenModal();
    toast.promise(
      storeCustomerAsync({
        ...data,
        phone: phone && parsePhoneNumber(phone, "FR")?.number,
        gender: gender?.value,
        companyCourseIds: companyCourses?.map((element) => element.id),
      }).unwrap,
      {
        pending: "Création de la personne en cours...",
        success: {
          render() {
            reset();
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                La personne a bien été créée
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            showOrHiddenModal();
            if (data?.status === 422) {
              return "Les champs que vous avez remplis semblent être incorrects.";
            }

            return "Une erreur est survenue";
          },
        },
      }
    );
  };

  const onError: SubmitErrorHandler<PersonForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      title="Ajouter un employé"
      requiredLabel
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <Row className="gx-3">
        <Col lg={6}>
          <Form.Group>
            <Form.Label>Genre :</Form.Label>
            <Controller
              control={control}
              name="gender"
              render={({ field }) => (
                <Select
                  options={GENDER_TYPES}
                  getOptionValue={(option) => option.value}
                  getOptionLabel={(option) => option.label}
                  onChange={field.onChange}
                  value={field.value}
                />
              )}
            />
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Prenom <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              placeholder="ex : Remi"
              type="text"
              {...register("firstname", {
                required: INVALID_FIRSTNAME_MESSAGE,
              })}
              isInvalid={!!errors.firstname}
            />
            <Form.Control.Feedback type="invalid">
              {errors.firstname?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Nom <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              placeholder="ex : Dupont"
              type="text"
              {...register("lastname", {
                required: INVALID_LASTNAME_MESSAGE,
              })}
              isInvalid={!!errors.lastname}
            />
            <Form.Control.Feedback type="invalid">
              {errors.lastname?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>Date d'anniversaire :</Form.Label>
            <Form.Control
              type="date"
              {...register("birthdate")}
              isInvalid={!!errors.birthdate}
            />
            <Form.Control.Feedback type="invalid">
              {errors.birthdate?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>Ville de naissance :</Form.Label>
            <Form.Control
              type="text"
              placeholder="ex : Paris 17"
              {...register("cityOfBirth")}
              isInvalid={!!errors.cityOfBirth}
            />
            <Form.Control.Feedback type="invalid">
              {errors.cityOfBirth?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>Intitulé du poste :</Form.Label>
            <Form.Control
              placeholder="ex : Développeur, Chef de projet, etc..."
              type="text"
              {...register("jobTitle")}
              isInvalid={!!errors.jobTitle}
            />
            <Form.Control.Feedback type="invalid">
              {errors.jobTitle?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>Type de contrat :</Form.Label>
            <Form.Control
              placeholder="ex : CDI, CDDE, CDD, etc..."
              type="text"
              {...register("contractType")}
              isInvalid={!!errors.contractType}
            />
            <Form.Control.Feedback type="invalid">
              {errors.contractType?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>Email :</Form.Label>
            <Form.Control
              placeholder="ex : ilan@groupe-pi.com"
              type="email"
              {...register("email", {
                pattern: {
                  value: EMAIL_REGEX,
                  message: INVALID_EMAIL2_MESSAGE,
                },
              })}
              isInvalid={!!errors.email}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>Numéro de téléphone :</Form.Label>
            <Form.Control
              placeholder="ex : +33 6 34 83 55 32"
              type="text"
              {...register("phone", {
                validate: (value) => {
                  if (value && !isValidPhoneNumber(value, "FR")) {
                    return INVALID_PHONE2_MESSAGE;
                  }
                },
              })}
              isInvalid={!!errors.phone}
            />
            <Form.Text>Ce champ doit être unique par employé.</Form.Text>
            <Form.Control.Feedback type="invalid">
              {errors.phone?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>Adresse</Form.Label>
            <Form.Control
              placeholder="ex : 3 Rue de la Paix, 75002 Paris"
              type="string"
              {...register("address")}
              isInvalid={!!errors.address}
            />
            <Form.Control.Feedback type="invalid">
              {errors.address?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={3}>
          <Form.Group className="mb-3">
            <Form.Label>Ville :</Form.Label>
            <Form.Control
              placeholder="ex : 3 Rue de la Paix, 75002 Paris"
              type="string"
              {...register("city")}
              isInvalid={!!errors.city}
            />
            <Form.Control.Feedback type="invalid">
              {errors.city?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={3}>
          <Form.Group className="mb-3">
            <Form.Label>Code postale: </Form.Label>
            <Form.Control
              placeholder="ex : 3 Rue de la Paix, 75002 Paris"
              type="string"
              {...register("zipCode")}
              isInvalid={!!errors.zipCode}
            />
            <Form.Control.Feedback type="invalid">
              {errors.zipCode?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>Numéro de sécurité sociale :</Form.Label>
            <Form.Control
              placeholder="ex : 0987654345678"
              type="number"
              {...register("socialSecurityNumber", {
                validate: {
                  isLength15: (value) => {
                    if (value && value.length === 15) {
                      return "Ce champ doit avoir exactement 15 caractères.";
                    }
                  },
                },
              })}
              isInvalid={!!errors.socialSecurityNumber}
            />
            <Form.Text>Ce champ doit être unique par employé.</Form.Text>
            <Form.Control.Feedback type="invalid">
              {errors.socialSecurityNumber?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>Date d'embauche :</Form.Label>
            <Form.Control
              type="date"
              {...register("employmentStartDate")}
              isInvalid={!!errors.employmentStartDate}
            />
            <Form.Control.Feedback type="invalid">
              {errors.employmentStartDate?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={12}>
          <Form.Group className="mb-3" style={{ zIndex: 99999999 }}>
            <Form.Label>Formations :</Form.Label>
            <Controller
              control={control}
              name="companyCourses"
              render={({ field }) => (
                <SelectPaginateAsync
                  loadOptions={loadCompanyCoursesOptions}
                  additional={defaultAdditional}
                  getOptionLabel={(option) => option.course!.name}
                  getOptionValue={(option) => option.id}
                  value={field.value}
                  isMulti
                  isClearable
                  onChange={field.onChange}
                />
              )}
            />
          </Form.Group>
        </Col>
      </Row>
    </Modal>
  );
};

export default forwardRef(StorePersonModal);
